import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

/**
 * EVA Progress - Composant 100% custom
 *
 * Barre de progression utilisant les tokens EVA (pas de PrimeNG).
 * Supporte mode déterminé/indéterminé, label et différentes couleurs.
 *
 * @example
 * // Progress simple
 * <eva-progress [value]="65"></eva-progress>
 *
 * // Progress avec label
 * <eva-progress
 *   [value]="65"
 *   [showValue]="true"
 *   label="Progression du projet"
 * ></eva-progress>
 *
 * // Progress indéterminé (loading)
 * <eva-progress [indeterminate]="true"></eva-progress>
 *
 * // Progress coloré
 * <eva-progress [value]="90" color="success"></eva-progress>
 */
@Component({
  selector: "eva-progress",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./eva-progress.component.html",
  styleUrls: ["./eva-progress.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaProgressComponent {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Valeur de progression (0-100) */
  @Input() value: number = 0;

  /** Mode indéterminé (animation de chargement) */
  @Input() indeterminate: boolean = false;

  /** Affiche la valeur en pourcentage */
  @Input() showValue: boolean = false;

  /** Label descriptif */
  @Input() label: string = "";

  /** Couleur de la barre */
  @Input() color: "primary" | "success" | "warning" | "danger" | "info" = "primary";

  /** Taille de la barre */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Barre striée animée */
  @Input() striped: boolean = false;

  /** Animation des stries */
  @Input() animated: boolean = false;

  // ==========================================================================
  // Computed
  // ==========================================================================

  get normalizedValue(): number {
    return Math.max(0, Math.min(100, this.value));
  }

  get displayValue(): string {
    return `${Math.round(this.normalizedValue)}%`;
  }

  get barStyle(): { [key: string]: string } {
    if (this.indeterminate) {
      return {};
    }
    return {
      width: `${this.normalizedValue}%`,
    };
  }
}
